import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PaymentStatus } from '@techspert-io/engagements';
import {
  ConfirmationDialogComponent,
  IConfirmationDialogData,
} from '@techspert-io/shared';
import { ToastService } from '@techspert-io/user-alerts';
import moment from 'moment';
import { switchMap, takeWhile, tap } from 'rxjs';
import { MaterialModule } from '../../../../shared/patterns/material-module';
import { FileService } from '../../../../shared/services/file.service';

import { IPaymentDashboardEngagement } from '../../../../admin-portal/payments/payments-models';
import { PaymentType } from '../../models/payments.models';
import {
  IMappedPayment,
  PaymentRecordType,
  PaymentsService,
} from '../../services/payments.service';

export interface INormalizedPayment {
  id: string;
  referenceId: string;
  name: string;
  email: string;
  title?: string;
  reason?: string;
  owner: string;
  amount: number;
  currency: string;
  actualPaymentCurrency: string;
  paymentType: PaymentType;
  unitsUsed?: number;
  unitsUsedAdjustment?: number;
  date: string;
  status: PaymentStatus;
}

interface IPaymentBatch extends Record<string, string | number> {
  name: string;
  recipientEmail: string;
  paymentReference: string;
  receiverType: string;
  amountCurrency: string;
  amount: number;
  sourceCurrency: string;
  targetCurrency: string;
  type: string;
}

@Component({
  selector: 'app-payment-actions',
  templateUrl: './payment-actions.component.html',
  styleUrls: ['./payment-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MaterialModule],
})
export class PaymentActionsComponent implements OnChanges {
  @Input() engagements: IPaymentDashboardEngagement[] = [];
  @Input() payments: IMappedPayment[] = [];
  @Input({ required: true }) type: PaymentRecordType;
  @Input() loading: boolean = true;
  @Input() disableMarkAsSent: boolean = true;

  @Output() statusUpdates = new EventEmitter<Record<string, PaymentStatus>>();

  private normalizedPayments: INormalizedPayment[] = [];

  constructor(
    readonly dialog: MatDialog,
    private toastService: ToastService,
    private paymentsService: PaymentsService,
    private cdr: ChangeDetectorRef,
    private fileService: FileService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.engagements && this.type === PaymentRecordType.Engagement) {
      this.normalizeEngagements();
    }

    if (changes.payments && this.type === PaymentRecordType.Payment) {
      this.normalizePayments();
    }
  }

  downloadReconciliationFile() {
    if (!this.verifySelection()) {
      return;
    }

    this.fileService.exportToCsv(this.constructReconciliationData(), {
      filename: `reconciliation-file-${this.getCurrentDateString()}`,
      bom: true,
    });
  }

  downloadPaymentsBatch() {
    if (!this.verifySelection()) {
      return;
    }

    const filename = `payments-batch-${this.getCurrentDateString()}`;
    this.fileService.exportToCsv(this.constructPaymentBatchData(), {
      filename,
      title: filename,
      bom: true,
    });
  }

  markAsSent() {
    if (this.disableMarkAsSent) {
      this.toastService.sendMessage(
        'Marking payments as sent is not permitted',
        'danger'
      );

      return;
    }

    if (!this.verifySelection()) {
      return;
    }

    const toUpdate = this.normalizedPayments.filter(
      ({ status, paymentType }) =>
        status === 'not-paid' && paymentType !== 'thirdParty'
    );
    const toUpdateCount = toUpdate.length;

    if (!toUpdateCount) {
      this.toastService.sendMessage(
        'All selected payments are third-party or already marked as sent',
        'danger'
      );

      return;
    }

    const paymentsEmailText = this.constructPaymentEmailsText(toUpdate);

    this.dialog
      .open<ConfirmationDialogComponent, IConfirmationDialogData, boolean>(
        ConfirmationDialogComponent,
        {
          width: '500px',
          data: {
            title: `Mark payment${this.getPluralSuffix(toUpdateCount)} as sent`,
            messages: [
              `This will mark ${toUpdateCount} payment${this.getPluralSuffix(toUpdateCount)} as sent.`,
              ...paymentsEmailText,
              'Are you sure you wish to proceed?',
            ],
            isDangerous: true,
          },
        }
      )
      .afterClosed()
      .pipe(
        takeWhile((result) => result),
        tap(() => {
          this.loading = true;
          this.cdr.detectChanges();
        }),
        switchMap(() =>
          this.paymentsService.markAsSent({
            paymentIds: toUpdate.map(({ id }) => id),
            type: this.type,
          })
        ),
        tap((paymentIds) => {
          this.loading = false;
          this.cdr.detectChanges();

          const updatedPayments = paymentIds.length;

          if (!paymentIds.length) {
            return;
          }

          this.toastService.sendMessage(
            `${updatedPayments} payment${this.getPluralSuffix(updatedPayments)} marked as sent`,
            'success'
          );

          this.statusUpdates.emit(
            Object.fromEntries(
              paymentIds.map((paymentId) => [paymentId, 'payment-sent'])
            )
          );
        })
      )
      .subscribe();
  }

  private constructPaymentEmailsText(payments: INormalizedPayment[]) {
    const paymentEmailsByType = payments.reduce<
      Record<PaymentType, Set<string>>
    >(
      (acc, { paymentType, email }) => ({
        ...acc,
        [paymentType]: new Set([...acc[paymentType], email]),
      }),
      {
        transferwise: new Set(),
        bankTransfer: new Set(),
        paypal: new Set(),
        cheque: new Set(),
        donateToCharity: new Set(),
        thirdParty: new Set(),
      }
    );

    const wise = paymentEmailsByType.transferwise.size;
    const bankTransfer = paymentEmailsByType.bankTransfer.size;
    const paypal = paymentEmailsByType.paypal.size;

    const total = wise + bankTransfer + paypal;

    if (!total) {
      return [];
    }

    return [
      `Additionally, ${total} email${this.getPluralSuffix(total)} will be sent to the expert(s) informing them that their payment(s) have been sent:`,
      ...(!!wise
        ? [
            `• ${wise} via Wise (including instructions to claim the payment${this.getPluralSuffix(wise)})`,
          ]
        : []),
      ...(!!bankTransfer ? [`• ${bankTransfer} via bank transfer`] : []),
      ...(!!paypal ? [`• ${paypal} via PayPal`] : []),
    ];
  }

  private normalizeEngagements() {
    this.normalizedPayments = this.engagements.map(
      ({
        engagementId,
        expertId,
        expertName,
        email,
        engagementTitle,
        owner,
        amountOwed,
        currency,
        actualPaymentCurrency,
        paymentType,
        unitsUsed,
        unitsUsedAdjustment,
        dateOfEngagement,
        paymentStatus,
      }) => ({
        id: engagementId,
        referenceId: expertId,
        name: expertName,
        email,
        title: engagementTitle,
        owner,
        amount: amountOwed,
        currency,
        actualPaymentCurrency,
        paymentType,
        unitsUsed,
        unitsUsedAdjustment,
        date: dateOfEngagement,
        status: paymentStatus,
      })
    );
  }

  private normalizePayments() {
    this.normalizedPayments = this.payments.map(
      ({
        paymentId,
        expertProfileId,
        expertName,
        email,
        reason,
        ownerName,
        amount,
        currency,
        actualPaymentCurrency,
        paymentType,
        dateCreated,
        status,
      }) => ({
        id: paymentId,
        referenceId: expertProfileId,
        name: expertName,
        email,
        reason,
        owner: ownerName,
        amount,
        currency,
        actualPaymentCurrency,
        paymentType,
        date: new Date(dateCreated * 1000).toISOString(),
        status,
      })
    );
  }

  private verifySelection() {
    const selectedPaymentsCount = this.normalizedPayments.length;

    if (!selectedPaymentsCount) {
      this.toastService.sendMessage(
        'Please select at least one payment',
        'danger'
      );
    }

    return !!selectedPaymentsCount;
  }

  private constructReconciliationData() {
    return this.normalizedPayments.map((payment) => ({
      Expert: payment.name,
      Email: payment.email,
      ...(payment.title && {
        Engagement: payment.title,
      }),
      ...(payment.reason && {
        Reason: payment.reason,
      }),
      Owner: payment.owner,
      'Amount owed': payment.amount,
      Currency: payment.currency,
      'Paid in currency': payment.actualPaymentCurrency,
      'Payment type': payment.paymentType,
      ...('unitsUsed' in payment &&
        'unitsUsedAdjustment' in payment && {
          'Units (adjustment)': `${payment.unitsUsed} (${payment.unitsUsedAdjustment})`,
        }),
      DOE: moment(payment.date).format('DD/MM/YYYY'),
    }));
  }

  private constructPaymentBatchData(): IPaymentBatch[] {
    const aggregated = this.normalizedPayments.reduce<
      Record<string, IPaymentBatch>
    >((acc, { referenceId, name, email, currency, amount }) => {
      if (acc[referenceId]) {
        acc[referenceId].amount += amount;
      } else {
        acc[referenceId] = {
          name,
          recipientEmail: email,
          paymentReference: referenceId?.substring(0, 10) || 'No Expert Id',
          receiverType: 'PRIVATE',
          amountCurrency: currency,
          amount,
          sourceCurrency: ['GBP', 'EUR'].includes(currency) ? currency : 'USD',
          targetCurrency: currency,
          type: 'EMAIL',
        };
      }
      return acc;
    }, {});

    return Object.values(aggregated);
  }

  private getCurrentDateString() {
    return new Date().toISOString().slice(0, 10);
  }

  private getPluralSuffix(count: number) {
    return count > 1 ? 's' : '';
  }
}
